import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { fetchAllRows } from '@/lib/supabaseHelpers';

const COLORS = ['#0F1C2E', '#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

interface Answer {
  question: string;
  answer: string;
}

const LiveAnswerDistribution = () => {
  const [answers, setAnswers] = useState<Answer[]>([]);

  const fetchData = async () => {
    const allAnswers = await fetchAllRows<Answer>('quiz_answers', 'question, answer');
    setAnswers(allAnswers);
  };

  useEffect(() => {
    fetchData();
    const channel = supabase
      .channel('live-answers')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'quiz_answers' }, (payload) => {
        const row = payload.new as Answer;
        setAnswers(prev => [...prev, row]);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // Group by question
  const questions = new Map<string, Map<string, number>>();
  answers.forEach(a => {
    if (!questions.has(a.question)) questions.set(a.question, new Map());
    const counts = questions.get(a.question)!;
    counts.set(a.answer, (counts.get(a.answer) || 0) + 1);
  });

  const questionData = Array.from(questions.entries()).map(([question, counts]) => ({
    question,
    data: Array.from(counts.entries())
      .map(([name, value]) => ({
        name: name.length > 20 ? name.substring(0, 20) + '…' : name,
        value,
        fullName: name,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8),
  }));

  if (questionData.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" /> Antwoord-distributie
        </h3>
        <p className="text-sm text-gray-400">Nog geen antwoorden ontvangen</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
        <BarChart3 className="w-4 h-4" /> Antwoord-distributie
        <span className="ml-auto flex items-center gap-1.5 text-xs font-medium text-emerald-600">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          LIVE
        </span>
      </h3>
      <div className="space-y-6 max-h-[600px] overflow-y-auto">
        {questionData.map((q, qi) => (
          <div key={q.question}>
            <p className="text-xs font-medium text-gray-600 mb-2 truncate" title={q.question}>{q.question}</p>
            <ResponsiveContainer width="100%" height={Math.max(80, q.data.length * 28)}>
              <BarChart data={q.data} layout="vertical" margin={{ left: 0, right: 10 }}>
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(val: number, _: any, entry: any) => [val, entry.payload.fullName]} />
                <Bar dataKey="value" fill={COLORS[qi % COLORS.length]} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LiveAnswerDistribution;
